import { useState, useEffect } from 'react';
import type { TypingLesson, TypingDrill, TypingAttempt, LessonProgress } from '../../lib/types';
import { getAllTypingAttempts, getAllProgress } from '../../lib/db';
import { typingLessons, typingDrills } from './data';
import TypingPractice from './TypingPractice';
import TypingStats from './TypingStats';

type ActiveItem = { type: 'lesson'; item: TypingLesson } | { type: 'drill'; item: TypingDrill };

const difficultyColors: Record<string, { bg: string; text: string; label: string }> = {
  beginner: { bg: '#e8f5e9', text: '#2e7d32', label: 'مبتدی' },
  intermediate: { bg: '#fff3e0', text: '#ef6c00', label: 'متوسط' },
  advanced: { bg: '#fce4ec', text: '#c62828', label: 'پیشرفته' },
};

export default function TypingModule() {
  const [active, setActive] = useState<ActiveItem | null>(null);
  const [attempts, setAttempts] = useState<TypingAttempt[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [showStats, setShowStats] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [allAttempts, allProgress] = await Promise.all([
        getAllTypingAttempts(),
        getAllProgress(),
      ]);
      setAttempts(allAttempts);
      const completed = new Set(
        (allProgress as LessonProgress[])
          .filter((p) => p.moduleId === 'typing')
          .map((p) => p.lessonId),
      );
      setCompletedIds(completed);
    } catch {
      // Silently handle DB errors
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // When returning from practice, reload data
  const handleBack = () => {
    setActive(null);
    loadData();
  };

  // If a lesson/drill is active, show the practice screen
  if (active) {
    return <TypingPractice lesson={active.item} onBack={handleBack} />;
  }

  // Compute overall stats
  const totalAttempts = attempts.length;
  const avgWpm =
    totalAttempts > 0
      ? Math.round(attempts.reduce((sum, a) => sum + a.wpm, 0) / totalAttempts)
      : 0;
  const avgAccuracy =
    totalAttempts > 0
      ? Math.round(attempts.reduce((sum, a) => sum + a.accuracy, 0) / totalAttempts)
      : 0;

  if (loading) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 40 }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>در حال بارگذاری...</p>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">تمرین تایپ</h1>

      {/* Overall stats */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-value" style={{ fontFamily: 'var(--font-en)' }}>
            {totalAttempts}
          </div>
          <div className="stat-label">تعداد تمرین</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontFamily: 'var(--font-en)' }}>
            {avgWpm}
          </div>
          <div className="stat-label">میانگین سرعت</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ fontFamily: 'var(--font-en)' }}>
            {avgAccuracy}%
          </div>
          <div className="stat-label">میانگین دقت</div>
        </div>
      </div>

      {/* Stats toggle */}
      {totalAttempts > 0 && (
        <button
          className="btn-secondary"
          onClick={() => setShowStats(!showStats)}
          style={{
            width: '100%',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          {showStats ? 'بستن آمار' : 'مشاهده آمار'}
        </button>
      )}

      {showStats && <TypingStats attempts={attempts} />}

      {/* Lessons section */}
      <section style={{ marginTop: 20 }}>
        <h2 style={{ fontSize: '1.15rem', marginBottom: 12 }}>درس‌ها</h2>
        {typingLessons.map((lesson) => {
          const diff = difficultyColors[lesson.difficulty];
          const completed = completedIds.has(lesson.id);
          return (
            <button
              key={lesson.id}
              onClick={() => setActive({ type: 'lesson', item: lesson })}
              className="card"
              style={{
                width: '100%',
                textAlign: 'right',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                border: completed
                  ? '2px solid var(--color-success)'
                  : '1px solid var(--color-border)',
                background: 'var(--color-surface)',
              }}
            >
              {/* Completion indicator */}
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: completed ? 'var(--color-success)' : 'var(--color-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {completed ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#fff',
                    }}
                  />
                )}
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                    {lesson.titleFa}
                  </span>
                  <span
                    style={{
                      fontSize: '0.7rem',
                      padding: '2px 8px',
                      borderRadius: 12,
                      background: diff.bg,
                      color: diff.text,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {diff.label}
                  </span>
                </div>
                <div
                  className="ltr"
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lesson.text}
                </div>
              </div>
            </button>
          );
        })}
      </section>

      {/* Drills section */}
      <section style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: '1.15rem', marginBottom: 12 }}>تمرین‌ها</h2>
        {typingDrills.map((drill, index) => {
          const diff = difficultyColors[drill.difficulty];
          const completed = completedIds.has(drill.id);
          return (
            <button
              key={drill.id}
              onClick={() => setActive({ type: 'drill', item: drill })}
              className="card"
              style={{
                width: '100%',
                textAlign: 'right',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 20px',
                border: completed
                  ? '2px solid var(--color-success)'
                  : '1px solid var(--color-border)',
                background: 'var(--color-surface)',
              }}
            >
              {/* Number badge */}
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  background: completed ? 'var(--color-success)' : 'var(--color-primary)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  fontFamily: 'var(--font-en)',
                }}
              >
                {completed ? (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>

              {/* Text */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 2,
                  }}
                >
                  <span style={{ fontWeight: 500, fontSize: '0.95rem' }}>
                    {drill.titleFa}
                  </span>
                  <span
                    style={{
                      fontSize: '0.65rem',
                      padding: '1px 6px',
                      borderRadius: 10,
                      background: diff.bg,
                      color: diff.text,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {diff.label}
                  </span>
                </div>
                <div
                  className="ltr"
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {drill.text}
                </div>
              </div>
            </button>
          );
        })}
      </section>
    </div>
  );
}
